// Maya tracking — public server functions that log conversation messages
// and conversion events to the database. Anonymous-friendly (uses
// anon_session_id), no auth required.
//
// SECURITY: These endpoints are unauthenticated and MUST NOT accept a
// caller-supplied user_id — that would let anyone attribute events to any
// registered account and pollute analytics. If a real Supabase session is
// present in the request (Authorization: Bearer <jwt>), we derive user_id
// server-side from the verified claims. Otherwise we store null.
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";

const EventTypeSchema = z.enum([
  "destination_viewed",
  "tour_viewed",
  "language_selected",
  "audio_preview_started",
  "audio_preview_completed",
  "premium_audio_unlocked",
  "paywall_shown",
  "checkout_started",
  "checkout_cancelled",
  "purchase_completed",
  "pass_purchased",
  "destination_purchased",
  "conversation_started",
  "message_sent",
  // Embudo comercial de Maya: recomendación → clic a ficha → checkout.
  "maya_recommendation_shown",
  "maya_tour_click",
  "maya_external_click",
  "maya_checkout_started",
  // Maya vendedora (venta dentro del chat).
  "maya_sale_search",
  "maya_sale_tour_recommended",
  "maya_sale_tour_viewed",
  "maya_sale_date_selected",
  "maya_sale_checkout_started",
  "maya_sale_payment_approved",
  "maya_sale_payment_declined",
  "maya_sale_booking_confirmed",
  "maya_sale_booking_abandoned",
  "maya_sale_recovered",
]);

export type MayaEventType = z.infer<typeof EventTypeSchema>;

async function admin() {
  const { supabaseAdmin } = await import(
    "@/integrations/supabase/client.server"
  );
  return supabaseAdmin;
}

/**
 * Derive the authenticated user id from the request's bearer token, if any.
 * Returns null for unauthenticated callers. Never trusts caller-supplied
 * user_id fields.
 */
async function resolveUserIdFromRequest(): Promise<string | null> {
  try {
    const request = getRequest();
    const authHeader = request?.headers?.get("authorization");
    if (!authHeader?.startsWith("Bearer ")) return null;
    const token = authHeader.slice("Bearer ".length).trim();
    if (!token) return null;

    const SUPABASE_URL = process.env.SUPABASE_URL;
    const SUPABASE_PUBLISHABLE_KEY = process.env.SUPABASE_PUBLISHABLE_KEY;
    if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) return null;

    const { createClient } = await import("@supabase/supabase-js");
    const sb = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    });
    const { data, error } = await sb.auth.getClaims(token);
    if (error || !data?.claims?.sub) return null;
    return data.claims.sub as string;
  } catch {
    return null;
  }
}

/** Fire-and-forget event log. Never throws to the caller. */
export const logMayaEvent = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        event_type: EventTypeSchema,
        anon_session_id: z.string().trim().max(80).optional(),
        conversation_id: z.string().uuid().optional(),
        destination: z.string().trim().max(120).optional(),
        topic: z.string().trim().max(60).optional(),
        lang: z.string().trim().max(8).optional(),
        amount_cents: z.number().int().optional(),
        currency: z.string().trim().max(8).optional(),
        metadata: z.record(z.unknown()).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }): Promise<{ ok: true }> => {
    try {
      const userId = await resolveUserIdFromRequest();
      const sb = await admin();
      await sb.from("maya_events").insert({
        event_type: data.event_type,
        anon_session_id: data.anon_session_id ?? null,
        user_id: userId,
        conversation_id: data.conversation_id ?? null,
        destination: data.destination ?? null,
        topic: data.topic ?? null,
        lang: data.lang ?? null,
        amount_cents: data.amount_cents ?? null,
        currency: data.currency ?? null,
        metadata: (data.metadata ?? null) as never,
      });
    } catch (err) {
      console.warn("[maya-tracking] event failed", err);
    }
    return { ok: true };
  });

/** Get or create a conversation for the given session/user. */
export const ensureMayaConversation = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        anon_session_id: z.string().trim().min(1).max(80),
        destination: z.string().trim().max(120).optional(),
        lang: z.string().trim().max(8).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }): Promise<{ id: string }> => {
    const userId = await resolveUserIdFromRequest();
    const sb = await admin();
    const { data: existing } = await sb
      .from("maya_conversations")
      .select("id")
      .eq("anon_session_id", data.anon_session_id)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (existing?.id) return { id: existing.id as string };

    const { data: row, error } = await sb
      .from("maya_conversations")
      .insert({
        anon_session_id: data.anon_session_id,
        user_id: userId,
        destination: data.destination ?? null,
        lang: data.lang ?? "es",
      })
      .select("id")
      .single();
    if (error || !row) throw new Error(error?.message ?? "create conversation failed");
    return { id: row.id as string };
  });

/** Append a message to a conversation. */
export const logMayaMessage = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        conversation_id: z.string().uuid(),
        anon_session_id: z.string().trim().min(1).max(80),
        role: z.enum(["user", "assistant", "system"]),
        content: z.string().trim().min(1).max(8000),
        destination: z.string().trim().max(120).optional(),
        topic: z.string().trim().max(60).optional(),
        lang: z.string().trim().max(8).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }): Promise<{ ok: true }> => {
    try {
      const userId = await resolveUserIdFromRequest();
      const sb = await admin();

      // Verify the caller owns the conversation. Ownership is proven by
      // matching either the authenticated user_id or the anon_session_id
      // that was used when the conversation was created. This prevents
      // arbitrary callers from injecting messages into other users'
      // conversations.
      const { data: conv } = await sb
        .from("maya_conversations")
        .select("id, user_id, anon_session_id, message_count")
        .eq("id", data.conversation_id)
        .maybeSingle();

      if (!conv) return { ok: true };
      const ownsByUser = userId != null && conv.user_id === userId;
      const ownsBySession =
        conv.anon_session_id != null &&
        conv.anon_session_id === data.anon_session_id;
      if (!ownsByUser && !ownsBySession) {
        console.warn("[maya-tracking] rejected message for unowned conversation");
        return { ok: true };
      }

      await sb.from("maya_messages").insert({
        conversation_id: data.conversation_id,
        role: data.role,
        content: data.content,
        destination: data.destination ?? null,
        topic: data.topic ?? null,
        lang: data.lang ?? null,
      });

      const next = (conv.message_count ?? 0) + 1;
      await sb
        .from("maya_conversations")
        .update({
          message_count: next,
          last_message_at: new Date().toISOString(),
          ...(data.destination && { destination: data.destination }),
          ...(data.lang && { lang: data.lang }),
        })
        .eq("id", data.conversation_id);
    } catch (err) {
      console.warn("[maya-tracking] message failed", err);
    }
    return { ok: true };
  });
